import React, { useEffect, useState, useMemo } from "react";
import { getAuth } from "firebase/auth";
import * as XLSX from "xlsx";
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

import {
    FiPlus,
    FiTrash2,
    FiEdit,
    FiUpload,
    FiX,
    FiMenu,
    FiSearch,
    FiCheckCircle,
    FiAlertTriangle,
    FiArrowRight,
    FiHash,
    FiLock,
    FiUnlock
} from "react-icons/fi";

import {
    doc,
    updateDoc,
    deleteDoc,
    addDoc,
    getFirestore,
    collection,
    getDocs,
    writeBatch,
    getDoc
} from "firebase/firestore";

import { useAuth } from "../../component/AuthProvider";

const MySwal = withReactContent(Swal);

// --- Toast ---
const Toast = MySwal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 2000,
    timerProgressBar: true,
});

const GrageRegister = () => {
    const [registeredCars, setRegisteredCars] = useState([]);
    const [searchPlate, setSearchPlate] = useState("");
    const [searchNumber, setSearchNumber] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const { role, user } = useAuth(); 
    const [userRole, setUserRole] = useState("operator");

    const pageSize = 100;

    // Modal States
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCar, setEditingCar] = useState(null);
    const [formData, setFormData] = useState({ building: "", number: "", plate: "", desc: "", phone: "" });

    // Excel Upload States
    const [excelFile, setExcelFile] = useState(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewData, setPreviewData] = useState({ newRecords: [], updatedRecords: [] });
    const [previewExcelDuplicates, setPreviewExcelDuplicates] = useState([]); 
    const [isUploading, setIsUploading] = useState(false);

    // Config State
    const [configData, setConfigData] = useState({
        name: "", address: "", status: "normal", FREE_MINUTES: 0,
        PAYMENT_GRACE_MINUTES: 0, PRICE_PER_HOUR: 0, HOURLY_RATE: 0,
        prepaidHours: 0, priceParking: 0,
    });

    const db = getFirestore();
    const auth = getAuth();

    const toggleSidebar = () => {
        const newState = !sidebarOpen;
        setSidebarOpen(newState);
        localStorage.setItem('sidebar', newState);
    };

    useEffect(() => {
        fetchRegisteredCars();
        fetchUserRole();
        fetchConfigData();
    }, []);

    const fetchRegisteredCars = async () => {
        if (!auth.currentUser) return;
        const recordsColRef = collection(db, "parkings", auth.currentUser.displayName, "garageRegisters");
        try {
            const snapshot = await getDocs(recordsColRef);
            const allCars = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setRegisteredCars(allCars);
        } catch (error) {
            console.error("Машины мэдээлэл татахад алдаа гарлаа:", error);
        }
    };

    const fetchConfigData = async () => {
        if (!auth.currentUser) return;
        const configDocRef = doc(db, "parkings", auth.currentUser.displayName);
        try {
            const docSnap = await getDoc(configDocRef);
            if (docSnap.exists()) {
                const data = docSnap.data().config || {};
                setConfigData(prev => ({ ...prev, ...data })); 
            }
        } catch (error) {
            console.error("Тохиргоо татахад алдаа гарлаа:", error);
        }
    };

    const fetchUserRole = async () => {
        if (!auth.currentUser || !auth.currentUser.email) return;
        try {
            const userDocRef = doc(db, "parkings", auth.currentUser.displayName, "users", auth.currentUser.email); 
            const userSnap = await getDoc(userDocRef);
            if(userSnap.exists()){
                const data = userSnap.data();
                setUserRole(data['role']==="master" ? "master" : data['role'] === "admin" ? "admin" : "operator");
            }
        } catch (err) {
            console.error("Эрх шалгахад алдаа гарлаа:", err);
        }
    };

    const toggleBlockCar = async (car) => {
        const result = await MySwal.fire({
            title: car.isBlocked ? 'Идэвхжүүлэх үү?' : 'Блоклох уу?',
            text: car.isBlocked ? "Энэ машин зогсоолд нэвтрэх эрхтэй болно." : "Энэ машин зогсоолд нэвтрэх боломжгүй болно.",
            icon: car.isBlocked ? 'question' : 'warning',
            showCancelButton: true,
            confirmButtonColor: car.isBlocked ? '#10b981' : '#ef4444', 
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Тийм',
            cancelButtonText: 'Цуцлах',
        });

        if (result.isConfirmed) {
            try {
                const carRef = doc(db, "parkings", auth.currentUser.displayName, "garageRegisters", car.id);
                await updateDoc(carRef, { isBlocked: !car.isBlocked, updatedAt: new Date() });            
                fetchRegisteredCars(); 
                Toast.fire({ icon: 'success', title: 'Амжилттай өөрчиллөө' });
            } catch (e) {
                console.error("Block toggle error", e);
                MySwal.fire('Алдаа!', 'Үйлдэл амжилтгүй боллоо.', 'error');
            }
        }
    };

    const formatPlate = (plate) => {
        if (!plate) return "";
        return String(plate).trim().toUpperCase().replace(/[\s\-·•]/g, '');
    };

    const openModal = (car = null) => {
        if (car) {
            setEditingCar(car);
            setFormData({
                building: car.building || "", number: car.number || "",
                plate: car.plate || "", desc: car.desc || "", phone: car.phone || ""
            });
        } else {
            setEditingCar(null);
            setFormData({ building: "", number: "", plate: "", desc: "", phone: "" });
        }
        setIsModalOpen(true);
    };

    const handleSaveCar = async () => {
        if (!formData.plate) return MySwal.fire({ icon: "info", title: "Машины дугаар оруулна уу!" });

        const formattedPlate = formatPlate(formData.plate);
        if (formattedPlate.length !== 7 || !formattedPlate.match(/^(\d{4})([A-ZА-ЯӨҮ]{3})$/)) {
            return MySwal.fire({ icon: "warning", title: "Дугаар буруу форматтай байна (Жишээ: 5050УБА)" });
        }

        const isDuplicate = registeredCars.some(car => {
            const existingPlate = formatPlate(car.plate);
            return editingCar ? (existingPlate === formattedPlate && car.id !== editingCar.id) : (existingPlate === formattedPlate);
        });

        if (isDuplicate) return MySwal.fire({ icon: "warning", title: `"${formattedPlate}" дугаар бүртгэлтэй байна!` });

        const currentUser = auth.currentUser;
        if (!currentUser) return;
        const colRef = collection(db, "parkings", currentUser.displayName, "garageRegisters");

        try {
            if (editingCar) {
                const carRef = doc(colRef, editingCar.id);
                await updateDoc(carRef, { ...formData, plate: formattedPlate, isBlocked: editingCar.isBlocked ?? false, updatedAt: new Date() });
                Toast.fire({ icon: "success", title: "Амжилттай шинэчиллээ!" });
            } else {
                await addDoc(colRef, { ...formData, plate: formattedPlate, isBlocked: false, createdAt: new Date() });
                Toast.fire({ icon: "success", title: "Амжилттай нэмэгдлээ!" });
            }
            setIsModalOpen(false);
            fetchRegisteredCars();
        } catch (error) {
            MySwal.fire({ icon: "error", title: "Алдаа гарлаа" });
        }
    };

    const handleDelete = async (id) => {
        const result = await MySwal.fire({
            title: 'Устгах уу?', text: "Энэ үйлдлийг буцаах боломжгүй!",
            icon: 'warning', showCancelButton: true,
            confirmButtonColor: '#ef4444', cancelButtonColor: '#64748b',
            confirmButtonText: 'Устгах', cancelButtonText: 'Цуцлах'
        });

        if (result.isConfirmed) {
            try {
                await deleteDoc(doc(db, "parkings", auth.currentUser.displayName, "garageRegisters", id));
                fetchRegisteredCars();
                Toast.fire({ icon: 'success', title: 'Амжилттай устгалал' });
            } catch (error) {
                console.error("Delete error:", error);
            }
        }
    };

    // --- EXCEL LOGIC ---
    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) setExcelFile(file);
    };

    const handleAnalyzeExcel = () => {
        if (!excelFile) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            const bstr = evt.target.result;
            const wb = XLSX.read(bstr, { type: "binary" });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const rawData = XLSX.utils.sheet_to_json(ws, { defval: "" }); 
            processExcelData(rawData);
        };
        reader.readAsBinaryString(excelFile);
    };

    const processExcelData = (data) => {
        const newRecords = [];
        const updatedRecords = [];
        const existingPlatesMap = new Map(registeredCars.map(car => [formatPlate(car.plate), car]));
        const excelPlateCount = {}; 
        const firstRowByPlate = {}; 

        data.forEach(row => {
            const rawPlate = row.plate || row['plate'] || row['Дугаар'] || row['dugaar'] || row['ДУГААР'];
            if (!rawPlate) return;

            const formattedPlate = formatPlate(rawPlate);
            if (!formattedPlate.match(/^(\d{4})([A-ZА-ЯӨҮ]{3})$/)) return;

            excelPlateCount[formattedPlate] = (excelPlateCount[formattedPlate] || 0) + 1;
            if (!firstRowByPlate[formattedPlate]) firstRowByPlate[formattedPlate] = row; 
        });

        setPreviewExcelDuplicates(Object.entries(excelPlateCount).filter(([_, count]) => count > 1).map(([plate, count]) => ({ plate, count })));

        Object.keys(firstRowByPlate).forEach(plateKey => {
            const row = firstRowByPlate[plateKey];
            const formattedPlate = formatPlate(row.plate || row['plate'] || row['Дугаар'] || row['dugaar'] || row['ДУГААР']);
            const rawStatus = String(row.status || row['Status'] || row['Төлөв'] || "").trim().toLowerCase();
            const isBlockedStatus = rawStatus === "блоклосон";

            const newCarData = {
                building: row.building || row['байр'] || "", number: row.number || row['Тоот'] || "",
                plate: formattedPlate, desc: row.desc || row['Тайлбар'] || "", phone: row.phone || row['Утас'] || "",
                isBlocked: isBlockedStatus 
            };

            if (existingPlatesMap.has(formattedPlate)) {
                updatedRecords.push({ id: existingPlatesMap.get(formattedPlate).id, old: existingPlatesMap.get(formattedPlate), new: newCarData });
            } else {
                newRecords.push(newCarData);
            }
        });

        setPreviewData({ newRecords, updatedRecords });
        setIsPreviewOpen(true); 
    };

    const confirmBatchUpload = async () => {
        setIsUploading(true);
        const batch = writeBatch(db);
        const currentUser = auth.currentUser;
        if (!currentUser) return;

        try {
            const newPlatesSet = new Set();
            previewData.newRecords.forEach(car => {
                if (newPlatesSet.has(car.plate)) return;
                newPlatesSet.add(car.plate);
                batch.set(doc(collection(db, "parkings", currentUser.displayName, "garageRegisters")), { ...car, createdAt: new Date() });
            });

            const updatedIdsSet = new Set();
            previewData.updatedRecords.forEach(item => {
                if (updatedIdsSet.has(item.id)) return;
                updatedIdsSet.add(item.id);
                batch.update(doc(db, "parkings", currentUser.displayName, "garageRegisters", item.id), { ...item.new, updatedAt: new Date() });
            });

            await batch.commit();

            MySwal.fire({
                title: "Амжилттай!",
                html: `Нэмсэн: <b>${previewData.newRecords.length}</b><br/>Шинэчилсэн: <b>${previewData.updatedRecords.length}</b>`,
                icon: "success", confirmButtonColor: "#3b82f6"
            });

            setIsPreviewOpen(false);
            setExcelFile(null);
            fetchRegisteredCars();
        } catch (error) {
            MySwal.fire({ title: "Алдаа гарлаа", icon: "error" });
        } finally {
            setIsUploading(false);
        }
    };

    // Filter Logic
    const filteredLogs = useMemo(() => {
        return registeredCars.filter((car) => {
            const matchPlate = !searchPlate || formatPlate(car.plate).includes(formatPlate(searchPlate));
            const matchNumber = !searchNumber || String(car.number || "").toLowerCase().includes(searchNumber.toLowerCase().replace(/\s/g, ""));
            return matchPlate && matchNumber;
        });
    }, [registeredCars, searchPlate, searchNumber]);

    const totalPages = Math.max(1, Math.ceil(filteredLogs.length / pageSize));
    const paginatedLogs = filteredLogs.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const handlePrev = () => setCurrentPage(prev => Math.max(prev - 1, 1));
    const handleNext = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));

    return (
        // ⭐️ Хамгийн гаднах хэсэгт max-w-[100vw] overflow-x-hidden нэмж дэлгэц халихаас сэргийллээ
        <div className="tw-bg-slate-50 tw-min-h-screen tw-p-3 sm:tw-p-6 lg:tw-p-8 tw-font-sans tw-text-slate-800 tw-max-w-[100vw] tw-overflow-x-hidden">
            
            {/* HEADER */}
            <div className="tw-flex tw-items-center tw-gap-4 tw-mb-6 sm:tw-mb-8">

                <div>
                    <h2 className="tw-text-xl sm:tw-text-2xl lg:tw-text-3xl tw-font-bold tw-text-slate-900 tw-tracking-tight">Бүртгэлийн жагсаалт</h2>
                    <p className="tw-text-xs sm:tw-text-sm tw-text-slate-500 tw-mt-1">Гаражид нэвтрэх эрхтэй машинуудын мэдээлэл</p>
                </div>
            </div>

            {/* CONTROLS (Add Manual & Excel) */}
            <div className="tw-bg-white tw-p-4 sm:tw-p-6 tw-rounded-2xl tw-shadow-sm tw-border tw-border-slate-200 tw-mb-6 sm:tw-mb-8 tw-flex tw-flex-col lg:tw-flex-row tw-gap-6 lg:tw-gap-8 lg:tw-divide-x tw-divide-slate-100">
                
                <div className="tw-flex-1">
                    <h4 className="tw-text-sm sm:tw-text-base tw-font-bold tw-text-slate-800 tw-mb-3 sm:tw-mb-4">Гар аргаар нэмэх</h4>
                    <button 
                        onClick={() => openModal()}
                        className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-w-full sm:tw-w-auto tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-px-6 tw-py-2.5 tw-rounded-xl tw-font-bold tw-transition-all tw-shadow-sm hover:tw-shadow active:tw-scale-95"
                    >
                        <FiPlus size={18}/> Шинэ машин нэмэх
                    </button>
                </div>

                <div className="tw-flex-1 lg:tw-pl-8">
                    <h4 className="tw-text-sm sm:tw-text-base tw-font-bold tw-text-slate-800 tw-mb-3 sm:tw-mb-4">Excel-ээс хуулах</h4>
                    <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-start sm:tw-items-center tw-gap-3">
                        <div className="tw-relative tw-w-full sm:tw-w-auto">
                            <input
                                type="file" accept=".xlsx, .xls"
                                onChange={handleFileSelect}
                                className="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-opacity-0 tw-cursor-pointer"
                            />
                            <div className="tw-bg-slate-50 tw-border tw-border-dashed tw-border-slate-300 hover:tw-border-blue-400 hover:tw-bg-blue-50 tw-text-slate-600 tw-px-4 tw-py-2.5 tw-rounded-xl tw-text-sm tw-font-medium tw-transition-colors tw-flex tw-items-center tw-justify-center sm:tw-justify-start tw-gap-2 tw-w-full">
                                <FiUpload /> {excelFile ? excelFile.name : "Файл сонгох..."}
                            </div>
                        </div>
                        
                        <button
                            onClick={handleAnalyzeExcel}
                            disabled={!excelFile}
                            className={`tw-flex tw-items-center tw-justify-center tw-gap-2 tw-w-full sm:tw-w-auto tw-px-6 tw-py-2.5 tw-rounded-xl tw-font-bold tw-transition-all tw-shadow-sm active:tw-scale-95 ${excelFile ? 'tw-bg-emerald-500 hover:tw-bg-emerald-600 tw-text-white hover:tw-shadow' : 'tw-bg-slate-100 tw-text-slate-400 tw-cursor-not-allowed'}`}
                        >
                            <FiCheckCircle size={18}/> Шалгах & Хуулах
                        </button>
                    </div>
                    <p className="tw-text-xs tw-text-slate-500 tw-mt-3 tw-leading-relaxed">
                        Баганын нэрс: <span className="tw-font-bold tw-bg-slate-100 tw-px-1 tw-rounded">plate</span>, <span className="tw-font-bold tw-bg-slate-100 tw-px-1 tw-rounded">building</span>, <span className="tw-font-bold tw-bg-slate-100 tw-px-1 tw-rounded">number</span>, <span className="tw-font-bold tw-bg-slate-100 tw-px-1 tw-rounded">desc</span>, <span className="tw-font-bold tw-bg-slate-100 tw-px-1 tw-rounded">phone</span>
                    </p>
                </div>
            </div>

            {/* SEARCH */}
            <div className="tw-bg-white tw-rounded-t-2xl tw-shadow-sm tw-border tw-border-slate-200 tw-border-b-0 tw-p-4 tw-flex tw-flex-col sm:tw-flex-row tw-gap-3 sm:tw-gap-4">
                <div className="tw-relative tw-w-full sm:tw-w-72">
                    <FiSearch className="tw-absolute tw-left-4 tw-top-1/2 tw--translate-y-1/2 tw-text-slate-400" size={18} />
                    <input
                        type="text" placeholder="Улсын дугаар..."
                        value={searchPlate} onChange={e => { setSearchPlate(e.target.value); setCurrentPage(1); }}
                        className="tw-w-full tw-pl-11 tw-pr-4 tw-py-2.5 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-xl tw-text-sm tw-font-medium tw-text-slate-800 focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500"
                    />
                </div>
                <div className="tw-relative tw-w-full sm:tw-w-48">
                    <FiHash className="tw-absolute tw-left-4 tw-top-1/2 tw--translate-y-1/2 tw-text-slate-400" size={18} />
                    <input
                        type="text" placeholder="Тоот..."
                        value={searchNumber} onChange={e => { setSearchNumber(e.target.value); setCurrentPage(1); }}
                        className="tw-w-full tw-pl-11 tw-pr-4 tw-py-2.5 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-xl tw-text-sm tw-font-medium tw-text-slate-800 focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500"
                    />
                </div>
            </div>

            {/* TABLE - ⭐️ tw-overflow-hidden болон overflow-x-auto нэмэгдсэн */}
            <div className="tw-bg-white tw-shadow-sm tw-border tw-border-slate-200 tw-rounded-b-2xl tw-w-full tw-overflow-hidden">
                <div className="tw-overflow-x-auto tw-w-full tw-block">
                    <table className="tw-w-full tw-text-left tw-border-collapse tw-min-w-[700px] md:tw-min-w-[800px]">
                        <thead className="tw-bg-slate-50 tw-border-b tw-border-slate-200">
                            <tr>
                                <th className="tw-px-4 sm:tw-px-6 tw-py-4 tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-w-12 sm:tw-w-16">№</th>
                                <th className="tw-px-3 sm:tw-px-4 tw-py-4 tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider">Байр / Тоот</th>
                                <th className="tw-px-3 sm:tw-px-4 tw-py-4 tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider">Улсын дугаар</th>
                                <th className="tw-px-3 sm:tw-px-4 tw-py-4 tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider">Холбогдох дугаар</th>
                                <th className="tw-px-3 sm:tw-px-4 tw-py-4 tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider">Тайлбар</th>
                                <th className="tw-px-3 sm:tw-px-4 tw-py-4 tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-text-center">Төлөв</th>
                                <th className="tw-px-4 sm:tw-px-6 tw-py-4 tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-text-right">Үйлдэл</th>
                            </tr>
                        </thead>
                        <tbody className="tw-divide-y tw-divide-slate-100">
                            {paginatedLogs.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="tw-px-6 tw-py-12 tw-text-center tw-text-slate-400">Мэдээлэл олдсонгүй.</td>
                                </tr>
                            ) : paginatedLogs.map((car, idx) => (
                                <tr key={car.id} className="hover:tw-bg-slate-50/80 tw-transition-colors">
                                    <td className="tw-px-4 sm:tw-px-6 tw-py-4 tw-text-xs sm:tw-text-sm tw-font-medium tw-text-slate-400">
                                        {(currentPage - 1) * pageSize + idx + 1}
                                    </td>
                                    <td className="tw-px-3 sm:tw-px-4 tw-py-4 tw-text-xs sm:tw-text-sm tw-font-medium tw-text-slate-700">
                                        {car.building} - {car.number}
                                    </td>
                                    <td className="tw-px-3 sm:tw-px-4 tw-py-4">
                                        <span className="tw-inline-block tw-px-2 sm:tw-px-2.5 tw-py-1 tw-bg-slate-100 tw-border tw-border-slate-200 tw-rounded-md tw-text-xs sm:tw-text-sm tw-font-bold tw-text-slate-800 tw-tracking-wider">
                                            {car.plate}
                                        </span>
                                    </td>
                                    <td className="tw-px-3 sm:tw-px-4 tw-py-4 tw-text-xs sm:tw-text-sm tw-text-slate-600">{car.phone || "-"}</td>
                                    <td className="tw-px-3 sm:tw-px-4 tw-py-4 tw-text-xs sm:tw-text-sm tw-text-slate-500 tw-truncate tw-max-w-[100px] sm:tw-max-w-[150px]" title={car.desc}>{car.desc || "-"}</td>
                                    
                                    <td className="tw-px-3 sm:tw-px-4 tw-py-4 tw-text-center">
                                        <button 
                                            onClick={() => toggleBlockCar(car)}
                                            className={`tw-relative tw-inline-flex tw-h-5 sm:tw-h-6 tw-w-10 sm:tw-w-11 tw-items-center tw-rounded-full tw-transition-colors focus:tw-outline-none ${!car.isBlocked ? 'tw-bg-emerald-500' : 'tw-bg-rose-500'}`}
                                            title={car.isBlocked ? "Блоклогдсон (Дарж нээх)" : "Идэвхтэй (Дарж блоклох)"}
                                        >
                                            <span className={`tw-inline-block tw-h-3.5 sm:tw-h-4 tw-w-3.5 sm:tw-w-4 tw-transform tw-rounded-full tw-bg-white tw-transition-transform tw-shadow-sm ${!car.isBlocked ? 'tw-translate-x-5 sm:tw-translate-x-6' : 'tw-translate-x-1'}`} />
                                        </button>
                                    </td>

                                    <td className="tw-px-4 sm:tw-px-6 tw-py-4">
                                        <div className="tw-flex tw-items-center tw-justify-end tw-gap-1 sm:tw-gap-2">
                                            <button onClick={() => openModal(car)} className="tw-p-1.5 sm:tw-p-2 tw-text-slate-400 hover:tw-text-blue-600 hover:tw-bg-blue-50 tw-rounded-lg tw-transition-colors">
                                                <FiEdit size={16} />
                                            </button>
                                            <button onClick={() => handleDelete(car.id)} className="tw-p-1.5 sm:tw-p-2 tw-text-slate-400 hover:tw-text-rose-600 hover:tw-bg-rose-50 tw-rounded-lg tw-transition-colors">
                                                <FiTrash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {filteredLogs.length > pageSize && (
                    <div className="tw-px-4 sm:tw-px-6 tw-py-4 tw-border-t tw-border-slate-200 tw-bg-slate-50 tw-flex tw-flex-col sm:tw-flex-row tw-items-center tw-justify-between tw-gap-3">
                        <div className="tw-text-xs sm:tw-text-sm tw-text-slate-500">Нийт <span className="tw-font-bold tw-text-slate-900">{filteredLogs.length}</span> бичлэг</div>
                        <div className="tw-flex tw-items-center tw-gap-2 tw-w-full sm:tw-w-auto tw-justify-between sm:tw-justify-end">
                            <button onClick={handlePrev} disabled={currentPage === 1} className="tw-flex-1 sm:tw-flex-none tw-px-3 sm:tw-px-4 tw-py-2 tw-rounded-lg tw-bg-white tw-border tw-border-slate-200 tw-text-xs sm:tw-text-sm tw-font-bold tw-text-slate-700 hover:tw-bg-slate-50 disabled:tw-opacity-50">Өмнөх</button>
                            <div className="tw-px-3 sm:tw-px-4 tw-py-2 tw-bg-white tw-border tw-border-slate-200 tw-rounded-lg tw-text-xs sm:tw-text-sm tw-font-bold tw-text-slate-700">{currentPage} / {totalPages}</div>
                            <button onClick={handleNext} disabled={currentPage === totalPages} className="tw-flex-1 sm:tw-flex-none tw-px-3 sm:tw-px-4 tw-py-2 tw-rounded-lg tw-bg-white tw-border tw-border-slate-200 tw-text-xs sm:tw-text-sm tw-font-bold tw-text-slate-700 hover:tw-bg-slate-50 disabled:tw-opacity-50">Дараах</button>
                        </div>
                    </div>
                )}
            </div>

            {/* --- ADD/EDIT MODAL (Tailwind) --- */}
            {isModalOpen && (
                <div className="tw-fixed tw-inset-0 tw-z-[1000] tw-bg-slate-900/40 tw-backdrop-blur-sm tw-flex tw-items-center tw-justify-center tw-p-4 tw-animate-in tw-fade-in tw-duration-200">
                    <div className="tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-w-full tw-max-w-md tw-overflow-hidden tw-animate-in tw-zoom-in-95 tw-duration-200">
                        <div className="tw-flex tw-justify-between tw-items-center tw-px-5 sm:tw-px-6 tw-py-4 tw-border-b tw-border-slate-100">
                            <h3 className="tw-text-base sm:tw-text-lg tw-font-bold tw-text-slate-800">{editingCar ? "Машин засах" : "Шинэ машин нэмэх"}</h3>
                            <button onClick={() => setIsModalOpen(false)} className="tw-text-slate-400 hover:tw-text-rose-500 tw-transition-colors"><FiX size={24} /></button>
                        </div>
                        <div className="tw-p-5 sm:tw-p-6 tw-space-y-4">
                            <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4">
                                <div className="tw-flex-1">
                                    <label className="tw-block tw-text-[10px] sm:tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-mb-1.5">Байрны нэр</label>
                                    <input type="text" value={formData.building} onChange={e => setFormData({ ...formData, building: e.target.value })} className="tw-w-full tw-px-3 sm:tw-px-4 tw-py-2 sm:tw-py-2.5 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-xl tw-text-xs sm:tw-text-sm focus:tw-bg-white focus:tw-border-blue-500 focus:tw-outline-none" placeholder="Жишээ: 101-р байр" />
                                </div>
                                <div className="tw-flex-1">
                                    <label className="tw-block tw-text-[10px] sm:tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-mb-1.5">Тоот</label>
                                    <input type="text" value={formData.number} onChange={e => setFormData({ ...formData, number: e.target.value })} className="tw-w-full tw-px-3 sm:tw-px-4 tw-py-2 sm:tw-py-2.5 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-xl tw-text-xs sm:tw-text-sm focus:tw-bg-white focus:tw-border-blue-500 focus:tw-outline-none" placeholder="Жишээ: 45" />
                                </div>
                            </div>
                            <div>
                                <label className="tw-block tw-text-[10px] sm:tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-mb-1.5">Улсын дугаар <span className="tw-text-rose-500">*</span></label>
                                <input type="text" value={formData.plate} onChange={e => setFormData({ ...formData, plate: e.target.value.toUpperCase() })} className="tw-w-full tw-px-3 sm:tw-px-4 tw-py-2 sm:tw-py-2.5 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-xl tw-text-xs sm:tw-text-sm tw-font-bold tw-tracking-widest focus:tw-bg-white focus:tw-border-blue-500 focus:tw-outline-none" placeholder="5050УБА" />
                            </div>
                            <div>
                                <label className="tw-block tw-text-[10px] sm:tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-mb-1.5">Холбогдох утас</label>
                                <input type="text" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="tw-w-full tw-px-3 sm:tw-px-4 tw-py-2 sm:tw-py-2.5 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-xl tw-text-xs sm:tw-text-sm focus:tw-bg-white focus:tw-border-blue-500 focus:tw-outline-none" placeholder="99112233" />
                            </div>
                            <div>
                                <label className="tw-block tw-text-[10px] sm:tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-mb-1.5">Тайлбар</label>
                                <input type="text" value={formData.desc} onChange={e => setFormData({ ...formData, desc: e.target.value })} className="tw-w-full tw-px-3 sm:tw-px-4 tw-py-2 sm:tw-py-2.5 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-xl tw-text-xs sm:tw-text-sm focus:tw-bg-white focus:tw-border-blue-500 focus:tw-outline-none" placeholder="Нэмэлт мэдээлэл..." />
                            </div>
                        </div>
                        <div className="tw-px-5 sm:tw-px-6 tw-py-4 tw-bg-slate-50 tw-border-t tw-border-slate-100 tw-flex tw-justify-end tw-gap-3">
                            <button onClick={() => setIsModalOpen(false)} className="tw-px-4 sm:tw-px-5 tw-py-2 sm:tw-py-2.5 tw-text-xs sm:tw-text-sm tw-font-bold tw-text-slate-600 hover:tw-bg-slate-200 tw-rounded-xl tw-transition-colors">Болих</button>
                            <button onClick={handleSaveCar} className="tw-px-4 sm:tw-px-6 tw-py-2 sm:tw-py-2.5 tw-text-xs sm:tw-text-sm tw-font-bold tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-rounded-xl tw-shadow-sm tw-transition-colors">Хадгалах</button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- EXCEL PREVIEW MODAL (Tailwind) --- */}
            {isPreviewOpen && (
                <div className="tw-fixed tw-inset-0 tw-z-[1001] tw-bg-slate-900/60 tw-backdrop-blur-sm tw-flex tw-items-center tw-justify-center tw-p-4 tw-animate-in tw-fade-in">
                    <div className="tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-w-full tw-max-w-4xl tw-max-h-[90vh] tw-flex tw-flex-col tw-overflow-hidden tw-animate-in tw-slide-in-from-bottom-4">
                        <div className="tw-flex tw-justify-between tw-items-center tw-px-4 sm:tw-px-6 tw-py-3 sm:tw-py-4 tw-bg-slate-800 tw-text-white">
                            <h3 className="tw-text-base sm:tw-text-lg tw-font-bold tw-flex tw-items-center tw-gap-2"><FiUpload /> Excel өгөгдөл шалгах</h3>
                            <button onClick={() => setIsPreviewOpen(false)} className="tw-text-slate-300 hover:tw-text-white"><FiX size={24} /></button>
                        </div>
                        
                        <div className="tw-flex-1 tw-overflow-y-auto tw-p-4 sm:tw-p-6 tw-bg-slate-50 custom-scrollbar">
                            
                            {/* Давхардал */}
                            {previewExcelDuplicates.length > 0 && (
                                <div className="tw-mb-4 sm:tw-mb-6 tw-bg-white tw-border tw-border-rose-200 tw-rounded-xl tw-overflow-hidden">
                                    <div className="tw-bg-rose-50 tw-px-3 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-border-b tw-border-rose-100 tw-flex tw-items-center tw-gap-2">
                                        <FiAlertTriangle className="tw-text-rose-500" />
                                        <h4 className="tw-font-bold tw-text-rose-700 tw-text-xs sm:tw-text-sm">Excel доторх давхардсан дугаарууд</h4>
                                    </div>
                                    <div className="tw-p-3 sm:tw-p-4 tw-flex tw-flex-wrap tw-gap-2">
                                        {previewExcelDuplicates.map((d, i) => (
                                            <span key={i} className="tw-inline-flex tw-items-center tw-gap-1 sm:tw-gap-1.5 tw-bg-rose-50 tw-border tw-border-rose-200 tw-text-rose-700 tw-px-2 sm:tw-px-3 tw-py-1 tw-rounded-lg tw-text-xs sm:tw-text-sm tw-font-bold">
                                                {d.plate} <span className="tw-bg-rose-200 tw-text-rose-800 tw-px-1 sm:tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] sm:tw-text-xs">{d.count}х</span>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Шинэчлэгдэх */}
                            {previewData.updatedRecords.length > 0 && (
                                <div className="tw-mb-4 sm:tw-mb-6 tw-bg-white tw-border tw-border-amber-200 tw-rounded-xl tw-overflow-hidden">
                                    <div className="tw-bg-amber-50 tw-px-3 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-border-b tw-border-amber-100 tw-flex tw-items-center tw-justify-between">
                                        <h4 className="tw-font-bold tw-text-amber-700 tw-text-xs sm:tw-text-sm tw-flex tw-items-center tw-gap-2">
                                            <FiAlertTriangle /> Бүртгэлтэй байгааг шинэчлэх
                                        </h4>
                                        <span className="tw-bg-amber-200 tw-text-amber-800 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] sm:tw-text-xs tw-font-bold">{previewData.updatedRecords.length}</span>
                                    </div>
                                    <div className="tw-overflow-x-auto">
                                        <table className="tw-w-full tw-text-left tw-text-xs sm:tw-text-sm">
                                            <thead className="tw-bg-slate-50 tw-text-slate-500">
                                                <tr><th className="tw-px-3 sm:tw-px-4 tw-py-2">Дугаар</th><th className="tw-px-3 sm:tw-px-4 tw-py-2">Хуучин</th><th></th><th className="tw-px-3 sm:tw-px-4 tw-py-2">Шинэ</th></tr>
                                            </thead>
                                            <tbody className="tw-divide-y tw-divide-slate-100">
                                                {previewData.updatedRecords.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td className="tw-px-3 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-font-bold">{item.new.plate}</td>
                                                        <td className="tw-px-3 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-text-slate-500 tw-text-[10px] sm:tw-text-xs">
                                                            Байр: {item.old.building}-{item.old.number}<br/>Утас: {item.old.phone}
                                                        </td>
                                                        <td className="tw-px-1 sm:tw-px-2 tw-text-slate-300"><FiArrowRight/></td>
                                                        <td className="tw-px-3 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-text-emerald-700 tw-font-medium tw-text-[10px] sm:tw-text-xs">
                                                            Байр: {item.new.building}-{item.new.number}<br/>Утас: {item.new.phone}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Шинээр нэмэгдэх */}
                            <div className="tw-bg-white tw-border tw-border-emerald-200 tw-rounded-xl tw-overflow-hidden">
                                <div className="tw-bg-emerald-50 tw-px-3 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-border-b tw-border-emerald-100 tw-flex tw-items-center tw-justify-between">
                                    <h4 className="tw-font-bold tw-text-emerald-700 tw-text-xs sm:tw-text-sm tw-flex tw-items-center tw-gap-2">
                                        <FiCheckCircle /> Шинээр нэмэгдэх
                                    </h4>
                                    <span className="tw-bg-emerald-200 tw-text-emerald-800 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] sm:tw-text-xs tw-font-bold">{previewData.newRecords.length}</span>
                                </div>
                                {previewData.newRecords.length > 0 ? (
                                    <div className="tw-overflow-x-auto">
                                        <table className="tw-w-full tw-text-left tw-text-xs sm:tw-text-sm">
                                            <thead className="tw-bg-slate-50 tw-text-slate-500">
                                                <tr><th className="tw-px-3 sm:tw-px-4 tw-py-2">Дугаар</th><th className="tw-px-3 sm:tw-px-4 tw-py-2">Байр/Тоот</th><th className="tw-px-3 sm:tw-px-4 tw-py-2">Утас</th><th className="tw-px-3 sm:tw-px-4 tw-py-2">Тайлбар</th></tr>
                                            </thead>
                                            <tbody className="tw-divide-y tw-divide-slate-100">
                                                {previewData.newRecords.map((car, idx) => (
                                                    <tr key={idx}>
                                                        <td className="tw-px-3 sm:tw-px-4 tw-py-2 tw-font-bold">{car.plate}</td>
                                                        <td className="tw-px-3 sm:tw-px-4 tw-py-2 tw-text-slate-600">{car.building}-{car.number}</td>
                                                        <td className="tw-px-3 sm:tw-px-4 tw-py-2 tw-text-slate-600">{car.phone}</td>
                                                        <td className="tw-px-3 sm:tw-px-4 tw-py-2 tw-text-slate-500 tw-truncate tw-max-w-[120px] sm:tw-max-w-[200px]">{car.desc}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="tw-p-4 sm:tw-p-6 tw-text-center tw-text-slate-400 tw-text-xs sm:tw-text-sm">Шинэ машин олдсонгүй.</div>
                                )}
                            </div>
                        </div>
                        
                        <div className="tw-bg-white tw-border-t tw-border-slate-200 tw-px-4 sm:tw-px-6 tw-py-3 sm:tw-py-4 tw-flex tw-flex-col sm:tw-flex-row tw-items-center tw-justify-between tw-gap-3 sm:tw-gap-0">
                            <div className="tw-text-xs sm:tw-text-sm tw-text-slate-500 tw-w-full sm:tw-w-auto tw-text-center sm:tw-text-left">
                                Нийт хуулах: <span className="tw-font-bold tw-text-slate-900 tw-text-base sm:tw-text-lg">{previewData.newRecords.length + previewData.updatedRecords.length}</span> машин
                            </div>
                            <div className="tw-flex tw-gap-2 sm:tw-gap-3 tw-w-full sm:tw-w-auto">
                                <button onClick={() => setIsPreviewOpen(false)} className="tw-flex-1 sm:tw-flex-none tw-px-4 sm:tw-px-5 tw-py-2 sm:tw-py-2.5 tw-rounded-xl tw-text-xs sm:tw-text-sm tw-font-bold tw-text-slate-600 hover:tw-bg-slate-100 tw-transition-colors">Болих</button>
                                <button 
                                    onClick={confirmBatchUpload}
                                    disabled={isUploading || (previewData.newRecords.length === 0 && previewData.updatedRecords.length === 0)}
                                    className="tw-flex-1 sm:tw-flex-none tw-px-4 sm:tw-px-6 tw-py-2 sm:tw-py-2.5 tw-rounded-xl tw-text-xs sm:tw-text-sm tw-font-bold tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-shadow-sm disabled:tw-bg-slate-300 disabled:tw-cursor-not-allowed tw-transition-all"
                                >
                                    {isUploading ? "Хадгалж байна..." : "Баталгаажуулах"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default GrageRegister;